import { analyticsTrackEvent } from '@core/utils/analytics';
import {
  HOME_PAGE_ITEM_TYPE,
  HOME_PAGE_VIEWED,
  HOME_SPACE_ITEM_TYPE,
  HOME_SPACE_MATERIAL_CARD_CLICKED,
  HOME_SPACE_NAVIGATION_CARD_CLICKED,
  HOME_VIEW_ALL_CLICKED,
  HOME_WIDGET_ITEM_TYPE,
  HOME_WIDGET_LOADED,
  HOME_WIDGET_ITEM_CLICKED,
  HOME_WIDGET_EMPTY_CTA_CLICKED,
  HOME_RECENT_UPDATES_INTERACTED,
  MATERIAL_ITEM_TYPE,
  NAVIGATION_SOURCE_HOME_PAGE,
  HOME_MESSAGES_ITEM_TYPE,
  HOME_ASSIGNMENTS_ITEM_TYPE,
} from '@core/constants/analytics';
import type { HomeViewRole } from '@app/constants/home';

export const trackHomeViewAllClicked = (
  navigationSource: string,
  count: number
) => {
  analyticsTrackEvent(HOME_VIEW_ALL_CLICKED, {
    itemType: HOME_WIDGET_ITEM_TYPE,
    navigationSource,
    count,
  });
};

export const trackHomePageViewed = (navigationSource: string) => {
  analyticsTrackEvent(HOME_PAGE_VIEWED, {
    itemType: HOME_PAGE_ITEM_TYPE,
    navigationSource,
  });
};

export const trackHomeWidgetLoaded = (
  navigationSource: string,
  count: number,
  role: HomeViewRole
) => {
  analyticsTrackEvent(HOME_WIDGET_LOADED, {
    itemType: HOME_WIDGET_ITEM_TYPE,
    navigationSource,
    count,
    // Explicit home view role — do not rely on classroom analytics context,
    // which stays stuck on the last opened space role (often educator).
    role,
  });
};

export const trackHomeSpaceNavigationCardClicked = (
  spaceId: string,
  role: HomeViewRole
) => {
  analyticsTrackEvent(HOME_SPACE_NAVIGATION_CARD_CLICKED, {
    navigationSource: NAVIGATION_SOURCE_HOME_PAGE,
    itemType: HOME_SPACE_ITEM_TYPE,
    itemId: spaceId,
    spaceId,
    // Explicit space-card role — do not rely on classroom analytics context,
    // which stays stuck on the last opened space role (often educator).
    role,
  });
};

export const trackHomeSpaceMaterialCardClicked = (
  materialId: string | null | undefined,
  spaceId: string,
  role: HomeViewRole
) => {
  analyticsTrackEvent(HOME_SPACE_MATERIAL_CARD_CLICKED, {
    navigationSource: NAVIGATION_SOURCE_HOME_PAGE,
    itemType: MATERIAL_ITEM_TYPE,
    itemId: materialId,
    spaceId,
    role,
  });
};

export const trackHomeWidgetItemClicked = ({
  navigationSource,
  itemType,
  itemId,
  itemIndex,
  spaceId,
  url,
  role,
}: {
  navigationSource: string;
  itemType: string;
  itemId?: string;
  itemIndex?: number;
  spaceId?: string;
  url?: string;
  role?: HomeViewRole;
}) => {
  analyticsTrackEvent(HOME_WIDGET_ITEM_CLICKED, {
    navigationSource,
    itemType,
    itemId,
    spaceId: spaceId || '',
    url,
    itemIndex: typeof itemIndex === 'number' ? itemIndex + 1 : undefined,
    role,
  });
};

export const trackHomeMessagesWidgetItemClicked = (
  itemId: string,
  navigationSource: string,
  itemIndex?: number
) => {
  trackHomeWidgetItemClicked({
    navigationSource,
    itemType: HOME_MESSAGES_ITEM_TYPE,
    itemId,
    itemIndex,
  });
};

export const trackHomeAssignmentsWidgetItemClicked = (
  itemId: string,
  navigationSource: string,
  itemIndex: number,
  spaceId?: string
) => {
  trackHomeWidgetItemClicked({
    navigationSource,
    itemType: HOME_ASSIGNMENTS_ITEM_TYPE,
    itemId,
    itemIndex,
    spaceId,
  });
};

export const trackHomeWidgetEmptyCtaClicked = (
  navigationSource: string,
  itemType: string,
  action: string,
  spaceId?: string,
  role?: HomeViewRole
) => {
  analyticsTrackEvent(HOME_WIDGET_EMPTY_CTA_CLICKED, {
    navigationSource,
    itemType,
    action,
    spaceId,
    classId: spaceId,
    role,
  });
};

export const trackHomeRecentUpdatesInteracted = (
  navigationSource: string,
  itemType: string,
  action: string,
  itemId?: string,
  spaceId?: string,
  role?: HomeViewRole
) => {
  analyticsTrackEvent(HOME_RECENT_UPDATES_INTERACTED, {
    navigationSource,
    itemType,
    action,
    itemId,
    spaceId: spaceId || '',
    role,
  });
};
